import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MeDto, Permission } from '@app/shared';
import { api, ApiError } from './api';
import { platform } from './platform';
import { Ctx, loadThemeMode, saveThemeMode, type AppCtx, type Route, type ThemeMode } from './state';
import { Nav, Toast, type Tab } from './ui';
import { Logo } from './brand/Logo';
import { Access } from './screens/Access';
import { Panels } from './screens/Panels';
import { Viewer } from './screens/Viewer';
import { Tasks } from './screens/Tasks';
import { More } from './screens/More';
import { Staff } from './screens/admin/Staff';
import { Equipment } from './screens/admin/Equipment';
import { Sessions } from './screens/admin/Sessions';

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'auto') return mode;
  return platform.prefersDark() ? 'dark' : 'light';
}

export function App() {
  const [me, setMe] = useState<MeDto | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('panels');
  const [route, setRoute] = useState<Route | null>(null);
  const [toastText, setToastText] = useState<string | null>(null);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(loadThemeMode);
  const [, setThemeTick] = useState(0);
  const started = useRef(false);

  const theme = resolveTheme(themeMode);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const bg = theme === 'dark' ? '#232323' : '#ffffff';
    platform.setChrome(bg, bg);
  }, [theme]);
  useEffect(() => platform.onThemeChange(() => setThemeTick((n) => n + 1)), []);

  const reloadMe = useCallback(async () => {
    try {
      setMe(await api.me());
      setBootError(null);
    } catch (e) {
      setBootError(e instanceof ApiError ? e.message : 'Не удалось загрузить данные');
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    platform.ready();
    void reloadMe();
  }, [reloadMe]);

  const active = me?.employee?.status === 'active';

  // Переход по ссылке из бота: startapp=staff или startapp=p_<id панели>.
  const handledStart = useRef(false);
  useEffect(() => {
    if (!active || !me || handledStart.current) return;
    handledStart.current = true;
    const sp = platform.startParam;
    if (sp === 'staff' && me.permissions.includes('staff.manage')) {
      setTab('more');
      setRoute({ name: 'staff' });
    } else if (sp?.startsWith('p_')) setRoute({ name: 'viewer', panelId: sp.slice(2) });
  }, [active, me]);

  const close = useCallback(() => setRoute(null), []);
  // Открытая панель остаётся на связи, пока её не отключат: «Назад» только сворачивает её.
  const [live, setLive] = useState<string | null>(null);
  useEffect(() => {
    if (route?.name === 'viewer') setLive(route.panelId);
  }, [route]);
  const endLive = useCallback(() => {
    setLive(null);
    setRoute((r) => (r?.name === 'viewer' ? null : r));
  }, []);
  const hideToast = useCallback(() => setToastText(null), []);
  useEffect(() => {
    platform.setBack(route ? close : null);
  }, [route, close]);

  const ctx = useMemo<AppCtx | null>(() => {
    if (!me) return null;
    return {
      me,
      can: (p: Permission) => me.permissions.includes(p),
      reloadMe,
      toast: setToastText,
      fail: (e: unknown) => {
        platform.haptic('error');
        setToastText(e instanceof Error ? e.message : 'Что-то пошло не так');
      },
      open: (r: Route) => setRoute(r),
      close,
      themeMode,
      setThemeMode: (m: ThemeMode) => {
        saveThemeMode(m);
        setThemeModeState(m);
      },
    };
  }, [me, reloadMe, close, themeMode]);

  if (!me || !ctx) {
    return (
      <main className="app">
        <div className="center">
          <span style={{ color: 'var(--logo)' }}>
            <Logo height={18} />
          </span>
          {bootError ? (
            <>
              <p className="error">{bootError}</p>
              <button type="button" className="btn btn-outline" onClick={() => void reloadMe()}>
                Повторить
              </button>
            </>
          ) : (
            <p className="muted">Загрузка…</p>
          )}
        </div>
      </main>
    );
  }

  if (!active) {
    return (
      <Ctx.Provider value={ctx}>
        <Access />
        <Toast text={toastText} onDone={hideToast} />
      </Ctx.Provider>
    );
  }

  const sub = route && route.name !== 'viewer' ? route : null;
  return (
    <Ctx.Provider value={ctx}>
      <main className="app">
        {sub?.name === 'staff' && <Staff />}
        {sub?.name === 'equipment' && <Equipment panelId={sub.panelId} />}
        {sub?.name === 'sessions' && <Sessions panelId={sub.panelId} />}
        {!sub && tab === 'panels' && <Panels />}
        {!sub && tab === 'tasks' && <Tasks />}
        {!sub && tab === 'more' && <More />}
      </main>
      <Nav
        tab={tab}
        badge={me.loginAttempts}
        onTab={(t) => {
          setRoute(null);
          setTab(t);
          platform.haptic('light');
        }}
      />
      {(live || route?.name === 'viewer') && (
        <Viewer
          key={route?.name === 'viewer' ? route.panelId : live!}
          panelId={route?.name === 'viewer' ? route.panelId : live!}
          hidden={route?.name !== 'viewer'}
          onClose={close}
          onEnd={endLive}
        />
      )}
      <Toast text={toastText} onDone={hideToast} />
    </Ctx.Provider>
  );
}
